import { styled } from '@material-ui/core';
import { RawBoxWalls } from 'Plugins/FloorPlan/Components/Widgets/BoxWalls/BoxWalls';
import { IRoom } from 'Plugins/FloorPlan/Models/IRoom';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';
import { CoordPoint } from 'Plugins/FloorPlan/Utils/Types';
import { SvgAttributesWith } from 'Plugins/FloorPlan/Widgets/SvgTypes';
import React from 'react';

import { BandType, LaizeBand, LaizeCalculatorResult } from '../../Laize';
import { LaizeBands } from '../../v3/LaizeBands';
import { LaizeBandItemV3 } from '../../v3/LaizeBandTrace';
import { useLaizeCalcDialog } from '../LaizeCalcDialogProvider';
import { RawMapUIRoomItems } from '../Widgets/MapUIRoomItems';
import {
    BandsDialog,
    calculateMapBoxWithPoints,
    generateMapBandsStyles,
    usePlanBandeFocusedBandId,
    useZoomLaizeRoomGesture,
} from './BandsDialog';
import {
    BandChip,
    BandsColorPalette,
    getLegendFocusStyle,
    getTextDimensions,
    LaizeBandProps,
    LegendConstants,
    LegendItem,
    LegendMapTitle,
    LegendPositionProps,
    legendYOffset,
} from './Widget';
import { getProduitPrincipal } from 'Web/Pages/Quote/Utils/UpdateQuestions';

export const BandsInRoomDialog = () => {
    const { zoomedBands, setZoomedBands, state, laizeResult } = useLaizeCalcDialog();
    const room = state.rooms?.find((r) => r.roomId === zoomedBands?.roomId)!;
    return !room ? null : (
        <BandsDialog id="BandsInRoomDialog" open close={setZoomedBands}>
            {room && (
                <Container>
                    <BandsInRoomContent room={room} laizeResult={laizeResult!} />
                </Container>
            )}
        </BandsDialog>
    );
};

const Container = styled('div')({ position: 'relative', width: '100%', height: '100%', overflowY: 'auto' });

type ContentProps = {
    setSvgWrapper?: (ref: React.RefObject<SVGSVGElement>) => void;
    room: IRoom;
    laizeResult: LaizeCalculatorResult;
    exportMode?: boolean;
};

const bandToDisplayTypes = [BandType.New, BandType.Reused]; //!\\ sans les chutes : BandType.Reusable

export const BandsInRoomContent = ({ setSvgWrapper, room, laizeResult, exportMode }: ContentProps) => {
    const svgWrapper = React.useRef<SVGSVGElement>(null);

    React.useEffect(() => {
        setSvgWrapper?.(svgWrapper);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svgWrapper.current, room.roomId]);

    const bands = (laizeResult?.bands_v3 || []).filter((item) => bandToDisplayTypes.includes(item.type));
    const bandDetails: LaizeBand[] = laizeResult?.roomsBandsDetails?.find((x) => x.roomId === room.roomId)?.bands || [];
    const bandInRoom = bands.filter((b) => b.roomId === room.roomId);
    const box = calculateMapBoxWithPoints(bandInRoom.flatMap((b) => b.points));

    if (!box) return null;
    const { viewBox, legendTitle, legendItems, map } = generateMapBandsStyles(box);
    const params: MapTracesProps = { bands, bandDetails, rooms: [room], transform: map.transform, exportMode };

    return (
        <svg
            id={`BandsInRoom-${room.roomId}`}
            ref={svgWrapper}
            xmlns="http://www.w3.org/2000/svg" //!\ IMPORTANT FOR EXPORT
            viewBox={`${viewBox.xMin} ${viewBox.yMin} ${viewBox.width} ${viewBox.height}`}
            width={viewBox.width}
            height={viewBox.height}>
            <MapBandTitle room={room} transform={legendTitle.transform} />
            <RawBoxWalls rooms={[room]} transform={map.transform} />
            <RawMapUIRoomItems roomItems={room.roomItems} transform={map.transform} />
            <MapBandTraces {...params} />
            <MapBandNumber {...params} />
            <MapLegend {...params} transform={legendItems.transform} />
        </svg>
    );
};

const MapBandTitle = ({ room, ...attrs }: SvgAttributesWith<{ room: IRoom }>) => {
    const { produitPrincipal } = getProduitPrincipal(room);
    const productCode = produitPrincipal?.code;
    return (
        <LegendMapTitle {...attrs}>
            {room.name}
            {productCode && <tspan style={{ fontSize: 24, fontWeight: 400, fill: '#424448' }}> - {productCode}</tspan>}
        </LegendMapTitle>
    );
};

type MapTracesProps = SvgAttributesWith<{
    bands: LaizeBandItemV3[];
    bandDetails: LaizeBand[];
    rooms: IRoom[];
    exportMode?: boolean;
}>;

//#region //* BANDES

const MapBandTraces = (props: MapTracesProps) => {
    const { bands, rooms, bandDetails, ...attrs } = props;
    const room = rooms[0];
    const bandsInRoom = bands.filter((b) => b.roomId === room.roomId);
    return (
        <g role="MapBandTraces" {...attrs}>
            {bandsInRoom.map((band, index) => {
                const props: LaizeBandProps = { band, bands, index, bandDetails, rooms };
                switch (band.type) {
                    case BandType.New:
                        return <LaizeBandNew key={`LaizeBandNew-${index}`} {...props} />;
                    case BandType.Reused:
                        return <LaizeBandReused key={`LaizeBandReused-${index}`} {...props} />;
                    default:
                        return null;
                }
            })}
        </g>
    );
};

const MapBandNumber = ({ bands, rooms = [], ...attrs }: MapTracesProps) => {
    const room = rooms[0];
    const bandsInRoom = bands.filter((b) => b.roomId === room.roomId);

    return (
        <g role="MapBandNumber" {...attrs}>
            {bandsInRoom.map((band) => (
                <NumberBandChip key={`NBC-${band.id}`} id={getBandIndex(band, bands)} points={band.points} />
            ))}
        </g>
    );
};

const LaizeBandNew = ({ band }: SvgAttributesWith<LaizeBandProps>) => {
    const { focused, gestures } = useZoomLaizeRoomGesture(band.id);
    const pathProps = toBandPathProps({
        points: band.points,
        fill: focused ? BandsColorPalette.BandNewHighlight : BandsColorPalette.BandNew,
    });

    return (
        <g id={`LaizeBandNew-bandId-${band.bandId}`} {...gestures}>
            <path {...pathProps} />
        </g>
    );
};

const LaizeBandReused = ({ band }: SvgAttributesWith<LaizeBandProps>) => {
    const { focused, gestures } = useZoomLaizeRoomGesture(band.id);
    const pathProps = toBandPathProps({
        points: band.points,
        fill: focused ? BandsColorPalette.BandReusedHighlight : BandsColorPalette.BandReused,
    });
    return (
        <g id={`LaizeBandReused-${band.bandId}`} {...gestures}>
            <path {...pathProps} />
        </g>
    );
};

type LaizeBandPathProps = { points: CoordPoint[]; fill: string; dashArray?: boolean };
const toBandPathProps = ({ points, fill, dashArray }: LaizeBandPathProps): React.SVGAttributes<any> => {
    const coords = points.map((p) => `${p.x},${p.y}`);
    return {
        d: `M${coords[0]} ${coords.map((coord) => `L${coord}`).join(' ')} L${coords[0]}`,
        stroke: BandsColorPalette.BandStroke,
        strokeWidth: 2,
        strokeOpacity: 1,
        strokeDasharray: dashArray ? '3 3' : undefined,
        fill,
        fillRule: 'evenodd',
    };
};

//#endregion

//#region //* LEGEND

const MapLegend = (props: MapTracesProps) => {
    const { bands, rooms, bandDetails, ...attrs } = props;
    let legendTextY = LegendConstants.LEGEND_TEXT_START_Y;
    const focusedBandId = usePlanBandeFocusedBandId();

    const room = rooms[0];
    const bandsInRoom = bands.filter((b) => b.roomId === room.roomId);

    return (
        <g role="MapLegend" {...attrs}>
            {bandsInRoom.map((band, index) => {
                const result = PreviewBandLegend({
                    band,
                    index,
                    rooms,
                    bands,
                    bandDetails,
                    posY: legendTextY,
                    focused: focusedBandId === band.id,
                });
                legendTextY += result.yOffset;
                return <result.DrawLegendItem key={`LegendItem-${index}`} />;
            })}
        </g>
    );
};

export const PreviewBandLegend = (props: SvgAttributesWith<LaizeBandProps & LegendPositionProps>) => {
    const { band, bands, rooms, index, bandDetails, posY, focused } = props;
    const title = getBandIndex(band, bands).toString();
    const subtitle = band.type !== BandType.Reused ? `Bande ${title}` : ` Bande réutilisée (Bande ${title})`;
    const dimens = getLegendDimensions({ band, index, bandDetails, bands, rooms });
    const styles = getLegendFocusStyle(band.type, focused);
    const yOffset = legendYOffset([dimens.legendText1, dimens.legendText2, dimens.legendText3]);

    return {
        posY,
        yOffset,
        DrawLegendItem: () => (
            <LegendItem
                id={`LegendItem-bandId-${band.bandId}`}
                posY={posY}
                title={title}
                subtitle={subtitle}
                text1={dimens.legendText1}
                text2={dimens.legendText2}
                text3={dimens.legendText3}
                fill={styles.fill}
            />
        ),
    };
};

const getLegendDimensions = ({ band, bandDetails }: LaizeBandProps) => {
    let textDimensions: string = '';
    switch (band.type) {
        case BandType.New:
            textDimensions = getTextDimensions(LaizeBands.getBandDimensions(band.pointsWithMargins || []));
            break;
        case BandType.Reused:
            textDimensions = getTextDimensions(LaizeBands.getBandDimensions(band.reusedRelativePoints || []));
            break;
        case BandType.Reusable:
            textDimensions = getTextDimensions(LaizeBands.getBandDimensions(band.points || []));
            break;
    }

    let leftOverDimensions: string = '';
    let surfaceCoveredDimensions: string = '';

    if (band.type !== BandType.Reusable && band.type !== BandType.Reused) {
        const correspondingBand = bandDetails.find((b) => b.id === band.bandId);
        if (correspondingBand) {
            //! On prends la première chute. A revoir avec #36360
            const leftOverCoords = correspondingBand.leftOverCoords?.[0];
            if (leftOverCoords) {
                leftOverDimensions = getTextDimensions(LaizeBands.getBandDimensions(leftOverCoords));
            }
            if (correspondingBand.surfaceCoveredCoords !== undefined) {
                surfaceCoveredDimensions = getTextDimensions({
                    width: LaizeBands.getBandHeight({ band: correspondingBand.surfaceCoveredCoords }),
                    height: LaizeBands.getBandWidth({ band: correspondingBand.surfaceCoveredCoords }),
                });
            }
        }
    }

    const legendText1 =
        textDimensions &&
        (band.type === BandType.Reused
            ? `Bande réutilisée : ${textDimensions}`
            : `Bande à utiliser : ${textDimensions}`);
    const legendText2 = surfaceCoveredDimensions && `Découpe : ${surfaceCoveredDimensions}`;
    const legendText3 = leftOverDimensions && `Bande à réutiliser : ${leftOverDimensions}`;

    return { legendText1, legendText2, legendText3 };
};

//#endregion

const NumberBandChip = ({ id, points }: React.PropsWithChildren<{ id: number; points: CoordPoint[] }>) => {
    const center = PointUtils.bboxCenter(points);
    return <BandChip transform={`translate(${center.x},${center.y}) scale(1)`}>{String(id)}</BandChip>;
};

const getBandIndex = (band: LaizeBandItemV3, bands: LaizeBandItemV3[]) => {
    const bandNewList = bands.filter((x) => x.type === BandType.New);
    const index = bandNewList.findIndex((x) => x.bandId === band.bandId) + 1;
    return index;
};
